import React, { useEffect, useState } from "react";
import HomeSliderController from "../controllers/HomeSliderController";
import { userService } from "../services/userService";
import Utils from "../utils/Utils";
import PlusIcon from "../icons/BusinessHub/PlusIcon";
import { toast } from "react-toastify";
import "../styles/HomePageSlider.css";
import XIcon from "../icons/XIcon.js";

const emptySliderForm = {
  sliderText: "",
  pageLinkUrl: "",
  displayOrder: "",
  countryName: "Lebanon",
  image: null,
  imageFileName: "",
};

function HomePageSlider() {
  const [sliderData, setSliderData] = useState([]);
  const [isAddSliderOpen, setIsAddSliderOpen] = useState(false);
  const [isAddingSlider, setIsAddingSlider] = useState(false);
  const [sliderModalMode, setSliderModalMode] = useState("add");
  const [editingSliderId, setEditingSliderId] = useState("");
  const [editingSliderImageUrl, setEditingSliderImageUrl] = useState("");
  const [addSliderForm, setAddSliderForm] = useState(emptySliderForm);
  const [addSliderPreview, setAddSliderPreview] = useState("");
  const [addSliderError, setAddSliderError] = useState("");

  const getSliderData = async () => {
    try {
      const SliderData = await HomeSliderController.getSlider();
      setSliderData(Array.isArray(SliderData.data) ? SliderData.data : []);
    } catch (error) {
      console.error("Error fetching slider data:", error);
    }
  };

  useEffect(() => {
    getSliderData();
  }, []);

  const handleEditSlider = async (sliderId, updatedData) => {
    const request = {
      displayOrder: Number(updatedData?.displayOrder) || 1,
      homepageSliderId: sliderId,
      pageLinkUrl: updatedData?.pageLinkUrl,
      sliderText: updatedData?.sliderText,
    };

    if (updatedData?.image) {
      request.image = {
        ...updatedData.image,
        countryName: "Lebanon",
      };
    }

    try {
      const result = await HomeSliderController.editSlider({
        Authorization: userService.getUser(),
        request,
      });
      if (Utils.isSuccessResponse(result)) {
        toast.success("Slider updated");
        await getSliderData();
        return true;
      }
      toast.error(result?.response?.data?.message || "Slider could not be updated");
      return false;
    } catch (error) {
      console.error("Error updating slider:", error);
      toast.error("Slider could not be updated");
      return false;
    }
  };
  const handleDeleteSlider = async (sliderId) => {
    const request = {
      homepageSliderId: sliderId,
    };
    try {
      const result = await HomeSliderController.deleteSlider({
        Authorization: userService.getUser(),
        request,
      });
    } catch (error) {
      console.error("Error deleting slider:", error);
    }
  };

  const handleAddSlider = async (newSliderData) => {
    const request = {
      displayOrder:
        Number(newSliderData.displayOrder) ||
        (Array.isArray(sliderData) ? sliderData.length + 1 : 1),
      image: {
        ...newSliderData.image,
        countryName: "Lebanon",
      },
      pageLinkUrl: newSliderData.pageLinkUrl,
      sliderText: newSliderData.sliderText,
    };
    try {
      const result = await HomeSliderController.addSlider({
        Authorization: userService.getUser(),
        request,
      });
      if (Utils.isSuccessResponse(result)) {
        toast.success("Slider added");
        await getSliderData();
        return true;
      }
      toast.error(result?.response?.data?.message || "Slider could not be added");
      return false;
    } catch (error) {
      console.error("Error adding slider:", error);
      toast.error("Slider could not be added");
      return false;
    }
  };

  const openAddSliderPopup = () => {
    setSliderModalMode("add");
    setEditingSliderId("");
    setEditingSliderImageUrl("");
    setAddSliderForm(emptySliderForm);
    setAddSliderPreview("");
    setAddSliderError("");
    setIsAddSliderOpen(true);
  };

  const openEditSliderPopup = (slider) => {
    setSliderModalMode("edit");
    setEditingSliderId(slider.homepageSliderId);
    setEditingSliderImageUrl(slider.dataUrl || "");
    setAddSliderForm({
      sliderText: slider.sliderText || "",
      pageLinkUrl: slider.pageLinkUrl || "",
      displayOrder: slider.displayOrder || "",
      countryName: "Lebanon",
      image: null,
      imageFileName: "",
    });
    setAddSliderPreview(slider.dataUrl || "");
    setAddSliderError("");
    setIsAddSliderOpen(true);
  };

  const closeAddSliderPopup = () => {
    if (isAddingSlider) return;
    setIsAddSliderOpen(false);
    setSliderModalMode("add");
    setEditingSliderId("");
    setEditingSliderImageUrl("");
    setAddSliderForm(emptySliderForm);
    setAddSliderPreview("");
    setAddSliderError("");
  };

  const handleAddSliderInputChange = (event) => {
    const { name, value } = event.target;
    setAddSliderForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddSliderImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = Utils.validateImageFile(file, 8);
    if (!validation.valid) {
      setAddSliderError(validation.message);
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await Utils.fileToBase64(file);
      const extension =
        file.name.split(".").pop()?.toLowerCase() ||
        file.type.split("/").pop() ||
        "png";
      const fileName = file.name.replace(/\.[^/.]+$/, "") || "slider-image";

      setAddSliderPreview(dataUrl);
      setAddSliderError("");
      setAddSliderForm((prev) => ({
        ...prev,
        imageFileName: file.name,
        image: {
          base64Data: dataUrl.split(",")[1] || dataUrl,
          extension,
          fileName,
        },
      }));
    } catch (error) {
      console.error("Error preparing slider image:", error);
      setAddSliderError("Image could not be prepared");
    }
  };

  const getSliderImageMetaFromUrl = (imageUrl) => {
    const fallback = {
      extension: "png",
      fileName: "slider-image",
    };

    try {
      const { pathname } = new URL(imageUrl);
      const rawName = pathname.split("/").pop() || "";
      const cleanName = decodeURIComponent(rawName).split("?")[0];
      const [fileName, extension] = cleanName.split(/\.(?=[^.]+$)/);

      return {
        extension: extension || fallback.extension,
        fileName: fileName || fallback.fileName,
      };
    } catch {
      return fallback;
    }
  };

  const buildImagePayloadFromDataUrl = (dataUrl, imageUrl) => {
    const imageMeta = getSliderImageMetaFromUrl(imageUrl);

    return {
      base64Data: dataUrl.split(",")[1] || dataUrl,
      extension: imageMeta.extension,
      fileName: imageMeta.fileName,
    };
  };

  const handleAddSliderSubmit = async (event) => {
    event.preventDefault();
    setAddSliderError("");

    if (!addSliderForm.sliderText.trim()) {
      setAddSliderError("Slider text is required");
      return;
    }

    // if (!addSliderForm.pageLinkUrl.trim()) {
    //   setAddSliderError("Page link URL is required");
    //   return;
    // }

    if (sliderModalMode === "add" && !addSliderForm.image) {
      setAddSliderError("Slider image is required");
      return;
    }

    setIsAddingSlider(true);
    let sliderPayload = addSliderForm;

    if (sliderModalMode === "edit" && !addSliderForm.image) {
      const originalImageBase64 = await Utils.imageUrlToBase64(
        editingSliderImageUrl,
      );

      if (!originalImageBase64) {
        setIsAddingSlider(false);
        setAddSliderError("Original slider image could not be prepared");
        return;
      }

      sliderPayload = {
        ...addSliderForm,
        image: buildImagePayloadFromDataUrl(
          originalImageBase64,
          editingSliderImageUrl,
        ),
      };
    }

    const saved =
      sliderModalMode === "edit"
        ? await handleEditSlider(editingSliderId, sliderPayload)
        : await handleAddSlider(sliderPayload);
    setIsAddingSlider(false);

    if (saved) {
      closeAddSliderPopup();
    }
  };

  return (
    <div className="home-page-slider">
      <div className="catalog-cards-slider-header">
        <div className="catalog-cards-slider-header-copy">
          <div className="catalog-cards-slider-header-title">Home Slider</div>
          <div className="catalog-cards-slider-header-subtitle">
            Manage homepage banners, links, and display order.
          </div>
        </div>
        <button
          type="button"
          className="catalog-cards-slider-header-button"
          onClick={openAddSliderPopup}
        >
          <span className="catalog-cards-slider-header-button-icon">
            <PlusIcon />
          </span>
          <span>Add Slider</span>
        </button>
      </div>
      <div className="catalog-cards-slider-container">
        {sliderData.map((slider) => (
          <div
            key={slider.homepageSliderId}
            className="catalog-card"
            data-slider-id={slider.homepageSliderId}
          >
            <div className="catalog-card-order-badge">
              #{slider.displayOrder}
            </div>
            <div className="catalogs-card-header">
              {slider.dataUrl ? (
                <img
                  src={slider.dataUrl}
                  alt={slider.sliderText || "Homepage slider"}
                  className="catalogs-products-imgs"
                />
              ) : null}
            </div>
            <div className="catalogs-card-footer">
              <div>{slider.sliderText}</div>
              <div className="catagory-card-footer-data">
                {slider.pageLinkUrl}
              </div>
              <div className="catalog-card-footer-actions">
                <button
                  type="button"
                  className="catalog-card-footer-button catalog-card-footer-button-edit"
                  onClick={() => openEditSliderPopup(slider)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="catalog-card-footer-button catalog-card-footer-button-delete"
                  onClick={() => handleDeleteSlider(slider.homepageSliderId)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {isAddSliderOpen ? (
        <div
          className="home-slider-modal-overlay"
          onClick={closeAddSliderPopup}
        >
          <form
            className="home-slider-modal"
            onClick={(event) => event.stopPropagation()}
            onSubmit={handleAddSliderSubmit}
          >
            <div className="home-slider-modal-header">
              <div>
                <div className="home-slider-modal-title">
                  {sliderModalMode === "edit" ? "Edit Slider" : "Add Slider"}
                </div>
                <div className="home-slider-modal-subtitle">
                  {sliderModalMode === "edit"
                    ? "Update the banner details and replace the image if needed."
                    : "Upload the banner and set where it should open."}
                </div>
              </div>
              <button
                type="button"
                className="home-slider-modal-close"
                onClick={closeAddSliderPopup}
                aria-label="Close add slider popup"
              >
                <XIcon />
              </button>
            </div>

            <div className="home-slider-modal-grid">
              <label className="home-slider-form-field">
                <span>
                  Slider Text <sup className="required">*</sup>
                </span>
                <input
                  name="sliderText"
                  type="text"
                  value={addSliderForm.sliderText}
                  onChange={handleAddSliderInputChange}
                  placeholder="Summer sale - up to 40% off"
                />
              </label>

              <label className="home-slider-form-field">
                <span>Page Link URL</span>
                <input
                  name="pageLinkUrl"
                  type="text"
                  value={addSliderForm.pageLinkUrl}
                  onChange={handleAddSliderInputChange}
                  placeholder="/products/summer-sale"
                />
              </label>

              <label className="home-slider-form-field">
                <span>Display Order</span>
                <input
                  name="displayOrder"
                  type="number"
                  min="1"
                  value={addSliderForm.displayOrder}
                  onChange={handleAddSliderInputChange}
                  placeholder={String(sliderData.length + 1)}
                />
              </label>

              {/* <label className="home-slider-form-field">
                <span>Country Name</span>
                <input
                  name="countryName"
                  type="text"
                  value={addSliderForm.countryName}
                  onChange={handleAddSliderInputChange}
                  placeholder="Lebanon"
                />
              </label> */}
            </div>

            <label className="home-slider-image-field">
              <span>Slider Image</span>
              <div className="home-slider-image-upload">
                {addSliderPreview ? (
                  <img src={addSliderPreview} alt="Slider preview" />
                ) : (
                  <div className="home-slider-image-placeholder">
                    Choose an image
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleAddSliderImageChange}
              />
              {addSliderForm.imageFileName ? (
                <div className="home-slider-image-file">
                  {addSliderForm.imageFileName}
                </div>
              ) : null}
            </label>

            {addSliderError ? (
              <div className="home-slider-modal-error">{addSliderError}</div>
            ) : null}

            <div className="home-slider-modal-actions">
              <button
                type="button"
                className="home-slider-modal-cancel"
                onClick={closeAddSliderPopup}
                disabled={isAddingSlider}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="home-slider-modal-submit"
                disabled={isAddingSlider}
              >
                {isAddingSlider
                  ? sliderModalMode === "edit"
                    ? "Saving..."
                    : "Adding..."
                  : sliderModalMode === "edit"
                    ? "Save Changes"
                    : "Add Slider"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default HomePageSlider;
